import React, { useState, useRef, useEffect } from "react";
import { Button } from "./button";
import { Input } from "./input";
import { Card, CardContent } from "./card";
import { Badge } from "./badge";
import { Checkbox } from "./checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";
import { Upload, X, CheckCircle2, AlertCircle, Loader2, FolderOpen, FileText, Trash2 } from "lucide-react";
import { getFY_YEAR } from "@/utils/sharepoint-env";

interface SharePointFileUploadProps {
  onFilesUploaded: (files: Array<{ name: string; path: string; cloudUrl?: string }>) => void;
  multiple?: boolean;
  accept?: string;
  className?: string;
  triggerText?: string;
  showReferenceInput?: boolean;
  referencePlaceholder?: string;
  sectionList?: string[]; // optional sections metadata for db.json
  sectionToFind?: string; // section to find files from SharePoint (for Select File button)
  folderName?: string; // folder name for db.json updates (default: "client")
  sharePointFiles?: Array<{ name: string }>; // Shared SharePoint files list from parent
  isLoadingSharePointFiles?: boolean; // Loading state from parent
  existingFileName?: string; // Name of existing file for this section (disables upload if provided)
  onRemoveFile?: () => void; // Callback when file is removed (to refresh parent state)
  /** When true, "Link from SharePoint" uses multi-choice checkboxes (document list). When false, single-select dropdown (IPE/IUC). Default false. */
  linkFromSharePointMultiple?: boolean;
}

interface UploadStatus {
  fileName: string;
  status: 'uploading' | 'success' | 'error';
  progress?: number;
  error?: string;
  webUrl?: string;
}

export function SharePointFileUpload({
  onFilesUploaded,
  multiple = true,
  accept = "*",
  className = "",
  triggerText = "Upload Files to SharePoint",
  showReferenceInput = false,
  referencePlaceholder = "Enter reference name",
  sectionList = [],
  sectionToFind = "",
  folderName = "client",
  sharePointFiles: sharedSharePointFiles = [],
  isLoadingSharePointFiles: sharedIsLoadingSharePointFiles = false,
  existingFileName = "",
  onRemoveFile,
  linkFromSharePointMultiple = false,
}: SharePointFileUploadProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [referenceName, setReferenceName] = useState<string>("");
  const [uploadStatuses, setUploadStatuses] = useState<UploadStatus[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedSharePointFile, setSelectedSharePointFile] = useState<string>("");
  const [pendingSharePointFile, setPendingSharePointFile] = useState<string>(""); // Single file (when linkFromSharePointMultiple is false)
  const [pendingSharePointFiles, setPendingSharePointFiles] = useState<string[]>([]); // Multi-choice (when linkFromSharePointMultiple is true)
  const [isUpdatingSection, setIsUpdatingSection] = useState(false);
  const [needsConfirmation, setNeedsConfirmation] = useState(false); // Track if confirmation is needed
  const [isRemovingFile, setIsRemovingFile] = useState(false); // Track if file removal is in progress
  const [localSharePointFiles, setLocalSharePointFiles] = useState<Array<{ name: string }>>([]);
  const [isLoadingLocalFiles, setIsLoadingLocalFiles] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Normalize file format - handle both string arrays and object arrays (backward compatibility)
  const normalizeFiles = (files: Array<{ name: string }> | string[]): Array<{ name: string }> => {
    if (files.length === 0) return [];
    // Check if first item is a string or object
    const firstItem = files[0];
    if (typeof firstItem === 'string') {
      return (files as string[]).map(name => ({ name }));
    }
    return files as Array<{ name: string }>;
  };

  // Use shared files if provided, otherwise use local state (for backward compatibility)
  const rawSharePointFiles = sharedSharePointFiles.length > 0 ? sharedSharePointFiles : localSharePointFiles;
  const sharePointFiles = normalizeFiles(rawSharePointFiles);
  const isLoadingSharePointFiles = sharedIsLoadingSharePointFiles || isLoadingLocalFiles;

  // Load files from SharePoint when component mounts or sectionToFind changes
  useEffect(() => {
    const loadSharePointFiles = async () => {
      // Only load if we have sectionToFind prop and no shared files are provided
      if (sectionToFind !== undefined && sharedSharePointFiles.length === 0) {
        setIsLoadingLocalFiles(true);
        try {
          if ((window as any).sharePointAPI?.findFilesBySection) {
            const result = await (window as any).sharePointAPI.findFilesBySection(
              sectionToFind || "", // Empty string to get all files
              folderName
            );
            
            if (result.success && result.data?.files) {
              setLocalSharePointFiles(result.data.files.map((name: string) => ({ name })));
            } else {
              console.error("Failed to load SharePoint files:", result.error);
              setLocalSharePointFiles([]);
            }
          }
        } catch (error) {
          console.error("Error loading SharePoint files:", error);
          setLocalSharePointFiles([]);
        } finally {
          setIsLoadingLocalFiles(false);
        }
      }
    };

    loadSharePointFiles();
  }, [sectionToFind, folderName, sharedSharePointFiles.length]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(files);
    setNeedsConfirmation(true); // Require confirmation before upload

    const statuses: UploadStatus[] = files.map(file => ({
      fileName: file.name,
      status: 'uploading' as const
    }));
    setUploadStatuses(statuses);
  };

  const handleSharePointFileChange = (fileName: string) => {
    setPendingSharePointFile(fileName);
    setNeedsConfirmation(true);
  };

  const handleSharePointFileToggle = (fileName: string) => {
    setPendingSharePointFiles((prev) =>
      prev.includes(fileName) ? prev.filter((f) => f !== fileName) : [...prev, fileName]
    );
    setNeedsConfirmation(true);
  };

  const handleSelectAllSharePointFiles = (checked: boolean) => {
    if (checked) setPendingSharePointFiles(sharePointFiles.map((f) => f.name));
    else setPendingSharePointFiles([]);
    setNeedsConfirmation(true);
  };

  const linkOneFileToSection = async (fileName: string, sectionToAdd: string): Promise<boolean> => {
    if ((window as any).sharePointAPI?.updateLibrariesInformation) {
      try {
        const res = await (window as any).sharePointAPI.updateLibrariesInformation({
          sectionCode: sectionToAdd,
          fileName,
        });
        if (res?.success) return true;
      } catch {}
    }
    if ((window as any).sharePointAPI?.updateFileSectionInDb) {
      try {
        const result = await (window as any).sharePointAPI.updateFileSectionInDb(
          fileName,
          sectionToAdd,
          folderName
        );
        return !!result?.success;
      } catch {
        return false;
      }
    }
    return false;
  };

  const handleConfirmSharePointFile = async () => {
    const sectionToAdd = sectionList?.[0];
    if (!sectionToAdd || !sectionList?.length) return;

    if (!linkFromSharePointMultiple) {
      const fileName = pendingSharePointFile;
      if (!fileName) return;
      setSelectedSharePointFile(fileName);
      setIsUpdatingSection(true);
      setNeedsConfirmation(false);
      try {
        const ok = await linkOneFileToSection(fileName, sectionToAdd);
        if (ok && onFilesUploaded) onFilesUploaded([{ name: fileName, path: fileName }]);
        setPendingSharePointFile("");
      } catch (error) {
        console.error("Error updating file section:", error);
        setPendingSharePointFile("");
      } finally {
        setIsUpdatingSection(false);
      }
      return;
    }

    const fileNames = pendingSharePointFiles;
    if (fileNames.length === 0) return;
    setSelectedSharePointFile(fileNames[0]);
    setIsUpdatingSection(true);
    setNeedsConfirmation(false);
    const linked: Array<{ name: string; path: string }> = [];
    try {
      for (const fileName of fileNames) {
        const ok = await linkOneFileToSection(fileName, sectionToAdd);
        if (ok) linked.push({ name: fileName, path: fileName });
      }
      if (linked.length > 0 && onFilesUploaded) onFilesUploaded(linked);
      setPendingSharePointFiles([]);
    } catch (error) {
      console.error("Error updating file section:", error);
      setPendingSharePointFiles([]);
    } finally {
      setIsUpdatingSection(false);
    }
  };

  const handleCancelConfirmation = () => {
    setNeedsConfirmation(false);
    setPendingSharePointFile("");
    setPendingSharePointFiles([]);
    setSelectedFiles([]);
    setUploadStatuses([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setUploadStatuses(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFileToSharePoint = async (file: File): Promise<{ success: boolean; webUrl?: string; error?: string }> => {
    try {
      // Get FY_YEAR from database dynamically
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      // Check if file already exists in db.json "client" section before uploading
      const targetFolderName = folderName || "client";
      if (targetFolderName === "client" && (window as any).sharePointAPI?.loadAllDocuments) {
        try {
          const existingDocsResult = await (window as any).sharePointAPI.loadAllDocuments();
          if (existingDocsResult?.success && Array.isArray(existingDocsResult.data)) {
            const fileExists = existingDocsResult.data.some(
              (doc: { name: string }) => doc.name === file.name
            );
            if (fileExists) {
              return {
                success: false,
                error: `File with file name "${file.name}" exists already.`
              };
            }
          }
        } catch (checkError) {
          // If check fails, log but continue with upload (don't block upload if check fails)
          console.warn("Failed to check existing files, proceeding with upload:", checkError);
        }
      }

      const base64Content = await fileToBase64(file);

      if ((window as any).sharePointAPI?.uploadFile) {
        const result = await (window as any).sharePointAPI.uploadFile({
          fileContent: base64Content,
          fileName: file.name,
          referenceValue: referenceName,
          folderName: targetFolderName,
          fyYear: fyYear,
          sectionList: sectionList || [],
        });

        if (result.success) {
          return { success: true, webUrl: result.data?.webUrl };
        } else {
          return { success: false, error: result.error || 'Upload failed' };
        }
      } else {
        throw new Error('SharePoint API not available');
      }
    } catch (error) {
      return {
        success: false,
        webUrl: undefined,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    setIsUploading(true);

    try {
      const uploadPromises = selectedFiles.map(async (file, index) => {
        setUploadStatuses(prev => prev.map((status, i) => 
          i === index ? { ...status, status: 'uploading' } : status
        ));

        const result = await uploadFileToSharePoint(file);

        // Update Libraries_Information.json and db.json with section information if upload was successful
        if (result.success && sectionList && sectionList.length > 0) {
          const sectionCode = sectionList[0];

          // 1) Update Libraries_Information.json (used by some dashboards)
          if ((window as any).sharePointAPI?.updateLibrariesInformation) {
            try {
              const librariesInfoResult = await (window as any).sharePointAPI.updateLibrariesInformation({
                sectionCode: sectionCode,
                fileName: file.name,
              });
              if (!librariesInfoResult.success) {
                console.warn("Failed to update Libraries_Information.json:", librariesInfoResult.error);
              } else {
                console.log("Libraries_Information.json updated successfully");
              }
            } catch (librariesInfoError) {
              console.warn("Error updating Libraries_Information.json:", librariesInfoError);
              // Don't fail the main operation if this fails
            }
          }

          // 2) Also update db.json "section" field so the file is discoverable by section filters
          if ((window as any).sharePointAPI?.updateFileSectionInDb) {
            try {
              const dbResult = await (window as any).sharePointAPI.updateFileSectionInDb(
                file.name,
                sectionCode,
                folderName || "client",
              );
              if (!dbResult?.success) {
                console.warn("Failed to update db.json section for file:", file.name, dbResult?.error);
              }
            } catch (dbError) {
              console.warn("Error updating db.json section for file:", file.name, dbError);
              // Non-fatal; upload is still considered successful
            }
          }
        }

        setUploadStatuses(prev => prev.map((status, i) => 
          i === index ? { 
            ...status, 
            status: result.success ? 'success' : 'error',
            webUrl: result.webUrl,
            error: result.error
          } : status
        ));

        return {
          name: file.name,
          path: file.name,
          cloudUrl: result.webUrl
        };
      });

      const results = await Promise.all(uploadPromises);
      const successfulUploads = results.filter(r => r.cloudUrl);

      if (successfulUploads.length > 0) {
        onFilesUploaded(successfulUploads);
      }

      setTimeout(() => {
        setSelectedFiles([]);
        setUploadStatuses([]);
        setReferenceName("");
        setNeedsConfirmation(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }, 2000);
    } catch (error) {
    } finally {
      setIsUploading(false);
    }
  };

  const getStatusIcon = (status: UploadStatus['status']) => {
    switch (status) {
      case 'uploading':
        return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
      case 'success':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: UploadStatus['status']) => {
    switch (status) {
      case 'uploading':
        return "border-blue-500/20 bg-blue-500/10 text-blue-300";
      case 'success':
        return "border-green-500/20 bg-green-500/10 text-green-300";
      case 'error':
        return "border-red-500/20 bg-red-500/10 text-red-300";
      default:
        return "border-gray-500/20 bg-gray-500/10 text-gray-300";
    }
  };

  const handleRemoveFile = async () => {
    if (!existingFileName || !sectionList || sectionList.length === 0) {
      return;
    }

    const sectionCode = sectionList[0]; // Use the first section code from the list
    
    setIsRemovingFile(true);
    try {
      // Update Libraries_Information.json to clear file_name
      // This will also remove all workspace sections from db.json
      if ((window as any).sharePointAPI?.updateLibrariesInformation && sectionCode) {
        try {
          const librariesInfoResult = await (window as any).sharePointAPI.updateLibrariesInformation({
            sectionCode: sectionCode,
            fileName: "", // Clear the file_name field
          });
          if (librariesInfoResult.success) {
            console.log("Libraries_Information.json updated and all workspace sections removed from db.json");
          } else {
            console.warn("Failed to update Libraries_Information.json:", librariesInfoResult.error);
            // Fallback: try removing just the current section code
            if ((window as any).sharePointAPI?.removeFileSection) {
              await (window as any).sharePointAPI.removeFileSection({
                fileName: existingFileName,
                sectionToRemove: sectionCode,
              });
            }
          }
        } catch (librariesInfoError) {
          console.warn("Error updating Libraries_Information.json:", librariesInfoError);
          // Fallback: try removing just the current section code
          if ((window as any).sharePointAPI?.removeFileSection) {
            try {
              await (window as any).sharePointAPI.removeFileSection({
                fileName: existingFileName,
                sectionToRemove: sectionCode,
              });
            } catch (fallbackError) {
              console.warn("Fallback db.json removal also failed:", fallbackError);
            }
          }
        }
      } else {
        // Fallback if updateLibrariesInformation is not available
        if ((window as any).sharePointAPI?.removeFileSection) {
          const result = await (window as any).sharePointAPI.removeFileSection({
            fileName: existingFileName,
            sectionToRemove: sectionCode,
          });
          if (!result.success) {
            console.error("Failed to remove file section:", result.error);
            alert(`Failed to remove file: ${result.error || "Unknown error"}`);
            return;
          }
        } else {
          console.error("SharePoint API not available");
          alert("SharePoint API not available");
          return;
        }
      }
      
      // Notify parent to refresh
      if (onRemoveFile) {
        onRemoveFile();
      }
    } catch (error) {
      console.error("Error removing file section:", error);
      alert(`Error removing file: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsRemovingFile(false);
    }
  };

  // If existing file is found, show it and disable all functionality
  if (existingFileName) {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="rounded-lg border border-green-500/20 bg-green-500/10 p-3">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            <span className="text-xs font-medium text-green-400">File Already Uploaded</span>
          </div>
          <div className="text-xs text-white/80 mt-1 break-words mb-2">
            <FileText className="h-3 w-3 inline mr-1" />
            {existingFileName}
          </div>
          <Button
            onClick={handleRemoveFile}
            disabled={isRemovingFile}
            size="sm"
            variant="outline"
            className="w-full border-red-500/20 text-red-400 hover:bg-red-500/10 hover:text-red-300 hover:border-red-500/30 text-xs h-7"
          >
            {isRemovingFile ? (
              <>
                <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                Removing...
              </>
            ) : (
              <>
                <Trash2 className="h-3 w-3 mr-1.5" />
                Remove File
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* SharePoint: single-select (IPE/IUC) or multi-choice (document list) */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-white/70">
          {linkFromSharePointMultiple ? "Link from SharePoint" : "Select from SharePoint"}
        </label>
        {isLoadingSharePointFiles ? (
          <div className="flex items-center gap-2 text-xs text-white/60 py-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            Loading files...
          </div>
        ) : sharePointFiles.length === 0 ? (
          <div className="text-xs text-white/50 py-2">No files found</div>
        ) : linkFromSharePointMultiple ? (
          <>
            <div className="rounded-md border border-white/10 bg-white/5 max-h-48 overflow-y-auto">
              <div className="p-2 border-b border-white/10 flex items-center gap-2">
                <Checkbox
                  id="sharepoint-select-all"
                  checked={pendingSharePointFiles.length === sharePointFiles.length && sharePointFiles.length > 0}
                  onCheckedChange={(c) => handleSelectAllSharePointFiles(!!c)}
                  disabled={isUpdatingSection}
                  className="border-white/30 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                />
                <label htmlFor="sharepoint-select-all" className="text-xs text-white/80 cursor-pointer select-none">
                  Select all ({sharePointFiles.length})
                </label>
              </div>
              <div className="p-2 space-y-1">
                {sharePointFiles.map((file, index) => (
                  <label
                    key={index}
                    className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-white/5 cursor-pointer"
                  >
                    <Checkbox
                      checked={pendingSharePointFiles.includes(file.name)}
                      onCheckedChange={() => handleSharePointFileToggle(file.name)}
                      disabled={isUpdatingSection}
                      className="border-white/30 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                    />
                    <span className="text-xs text-white/90 truncate flex-1">{file.name}</span>
                  </label>
                ))}
              </div>
            </div>
            {needsConfirmation && pendingSharePointFiles.length > 0 && (
              <div className="flex gap-2 mt-2">
                <Button
                  onClick={handleConfirmSharePointFile}
                  disabled={isUpdatingSection}
                  size="sm"
                  className="flex-1 bg-green-600 text-white hover:bg-green-700 text-xs h-7"
                >
                  {isUpdatingSection ? (
                    <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> Linking...</>
                  ) : (
                    `Link ${pendingSharePointFiles.length} file(s)`
                  )}
                </Button>
                <Button
                  onClick={handleCancelConfirmation}
                  disabled={isUpdatingSection}
                  size="sm"
                  variant="outline"
                  className="flex-1 border-white/20 text-white/80 hover:bg-white/10 text-xs h-7"
                >
                  Cancel
                </Button>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="relative">
              <Select
                value={pendingSharePointFile || selectedSharePointFile}
                onValueChange={handleSharePointFileChange}
                disabled={isUpdatingSection || needsConfirmation}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select file" />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-black/90 text-white">
                  {sharePointFiles.map((file, index) => (
                    <SelectItem key={index} value={file.name}>{file.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(isUpdatingSection || isLoadingSharePointFiles) && (
                <div className="absolute right-8 top-1/2 -translate-y-1/2">
                  <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
                </div>
              )}
            </div>
            {needsConfirmation && pendingSharePointFile && (
              <div className="flex gap-2 mt-2">
                <Button
                  onClick={handleConfirmSharePointFile}
                  disabled={isUpdatingSection}
                  size="sm"
                  className="flex-1 bg-green-600 text-white hover:bg-green-700 text-xs h-7"
                >
                  {isUpdatingSection ? <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> Linking...</> : "Confirm"}
                </Button>
                <Button
                  onClick={handleCancelConfirmation}
                  disabled={isUpdatingSection}
                  size="sm"
                  variant="outline"
                  className="flex-1 border-white/20 text-white/80 hover:bg-white/10 text-xs h-7"
                >
                  Cancel
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Upload Local Files Button */}
      <div className="flex items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept={accept}
          onChange={handleFileSelect}
          className="hidden"
          id="sharepoint-file-upload"
        />
        <Button
          variant={'default'}
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-1.5 h-8 text-xs w-full"
        >
          <FolderOpen className="h-3 w-3" />
          Upload Local Files
        </Button>
      </div>

      {/* Reference Input - Only show when files are selected */}
      {showReferenceInput && selectedFiles.length > 0 && (
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-white/70">
            Reference (Optional)
          </label>
          <Input
            type="text"
            placeholder={referencePlaceholder}
            value={referenceName}
            onChange={(e) => setReferenceName(e.target.value)}
            className="bg-white/5 border-white/20 text-white placeholder:text-white/50 h-8 text-xs"
          />
        </div>
      )}

      {selectedFiles.length > 0 && (
        <div className="space-y-2 rounded-lg border border-white/10 bg-white/5 p-3">
          <div className="text-xs font-medium text-white/80 mb-2">Selected Files</div>
          {selectedFiles.map((file, index) => {
            const status = uploadStatuses[index];
            return (
              <div
                key={index}
                className="flex items-center justify-between rounded-md border border-white/10 bg-white/5 p-2"
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {getStatusIcon(status?.status || 'uploading')}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-white truncate">{file.name}</div>
                    <div className="text-[10px] text-white/60">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                    {status?.error && (
                      <div className="text-[10px] text-red-400 mt-0.5 truncate">
                        {status.error}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {status && (
                    <Badge className={`text-[10px] px-1.5 py-0 ${getStatusColor(status.status)}`}>
                      {status.status}
                    </Badge>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFile(index)}
                    className="h-6 w-6 p-0 text-white/60 hover:text-white hover:bg-white/10"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            );
          })}

          <div className="pt-2 space-y-2">
            {needsConfirmation && selectedFiles.length > 0 && (
              <div className="flex gap-2">
                <Button
                  onClick={handleUpload}
                  disabled={isUploading}
                  size="sm"
                  className="flex-1 bg-green-600 text-white hover:bg-green-700 text-xs h-8"
                >
                  Confirm & Upload
                </Button>
                <Button
                  onClick={handleCancelConfirmation}
                  disabled={isUploading}
                  size="sm"
                  variant="outline"
                  className="flex-1 border-white/20 text-white/80 hover:bg-white/10 text-xs h-8"
                >
                  Cancel
                </Button>
              </div>
            )}
            {!needsConfirmation && selectedFiles.length > 0 && (
              <Button
                onClick={handleUpload}
                disabled={isUploading}
                size="sm"
                className="w-full bg-green-600 text-white hover:bg-green-700 text-xs h-8"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-3 w-3 mr-1.5" />
                    Upload to SharePoint
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
